import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { apiClient } from "@/lib/api";
import { SITE_URL, SITE_BRAND } from "@/lib/seo-config";
import { METIER_TYPES, getCityMultiplier, priceWithMult } from "@/lib/metier-types";
import CityProspectForm from "@/components/CityProspectForm";
import QuickMissionForm from "@/components/QuickMissionForm";
import {
  Wrench, Zap, Home, Hammer, Key, Layers, Building, TreePine, Square,
  UtensilsCrossed, Trees, Flame, Bath, Warehouse, Bell, DoorClosed, Wind,
  Snowflake, Shovel, Paintbrush, ArrowRight, Lock, Camera, BadgeCheck,
  Grid3x3, Clock,
} from "lucide-react";

const ICONS = {
  Wrench, Zap, Home, Hammer, Key, Layers, Building, TreePine, Square,
  UtensilsCrossed, Trees, Flame, Bath, Warehouse, Bell, DoorClosed, Wind,
  Snowflake, Shovel, Paintbrush, Grid3x3,
  Bricks: Layers,
};

function buildSEO(m, citySlug, cityLabel) {
  const mult = getCityMultiplier(citySlug);
  const [lowH, highH] = priceWithMult(m.hourlyRate, mult);
  return {
    title: `${m.label} à ${cityLabel} : trouver un ${m.label.toLowerCase()} de confiance · ${SITE_BRAND}`,
    description: `${m.label} ${cityLabel} : ${m.intro} Prix ${lowH}-${highH}€/h à ${cityLabel}. SIRET vérifié, paiement sécurisé.`,
    keywords: `${m.label.toLowerCase()} ${cityLabel}, trouver ${m.label.toLowerCase()} ${cityLabel}, prix ${m.label.toLowerCase()} ${cityLabel}, devis ${m.label.toLowerCase()} ${cityLabel}, ${m.label.toLowerCase()} pas cher ${cityLabel}, meilleur ${m.label.toLowerCase()} ${cityLabel}`,
    canonical: `/artisan/${m.slug}/${citySlug}`,
    ogType: "website",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": `${m.label} à ${cityLabel} — ${SITE_BRAND}`,
        "description": m.intro,
        "provider": { "@type": "Organization", "name": SITE_BRAND, "url": SITE_URL },
        "areaServed": { "@type": "City", "name": cityLabel },
        "offers": {
          "@type": "AggregateOffer",
          "priceCurrency": "EUR",
          "lowPrice": priceWithMult(m.interventionRange, mult)[0],
          "highPrice": priceWithMult(m.interventionRange, mult)[1],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": `${m.label} · ${cityLabel} · ${SITE_BRAND}`,
        "url": `${SITE_URL}/artisan/${m.slug}/${citySlug}`,
        "description": `${m.label} SIRET vérifié à ${cityLabel} avec paiement sécurisé Batisur.`,
        "areaServed": { "@type": "City", "name": cityLabel },
        "priceRange": "€€",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Artisans", "item": `${SITE_URL}/artisan` },
          { "@type": "ListItem", "position": 3, "name": m.label, "item": `${SITE_URL}/artisan/${m.slug}` },
          { "@type": "ListItem", "position": 4, "name": cityLabel, "item": `${SITE_URL}/artisan/${m.slug}/${citySlug}` },
        ],
      },
    ],
  };
}

export default function MetierCityPage() {
  const { metier: mSlug, city: citySlug } = useParams();
  const m = METIER_TYPES[mSlug];
  const [cityData, setCityData] = useState(null);
  const [cityError, setCityError] = useState(false);

  useEffect(() => {
    if (citySlug) {
      apiClient
        .get(`/public/cities/${citySlug}`)
        .then(({ data }) => setCityData(data))
        .catch(() => setCityError(true));
    }
  }, [citySlug]);

  if (!m) return <Navigate to="/artisan" replace />;
  if (cityError) return <Navigate to={`/artisan/${mSlug}`} replace />;

  const Icon = ICONS[m.icon] || Wrench;
  const cityLabel = cityData?.city_label || (citySlug.charAt(0).toUpperCase() + citySlug.slice(1));
  const mult = getCityMultiplier(citySlug);
  const [lowH, highH] = priceWithMult(m.hourlyRate, mult);
  const [lowI, highI] = priceWithMult(m.interventionRange, mult);
  const [lowP, highP] = priceWithMult(m.exampleProject.price, mult);
  const isProspected = cityData?.prospected;

  return (
    <div className="min-h-screen bg-[#F7F7F9]" data-testid={`metier-city-${m.slug}-${citySlug}`}>
      <SEO cfg={buildSEO(m, citySlug, cityLabel)} />
      <Navbar />

      {/* HERO */}
      <section className="bg-[#0A0A0B] text-white border-b-2 border-[#0A0A0B] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 relative">
          <nav className="mono text-[11px] uppercase tracking-widest text-zinc-400 mb-5" aria-label="Fil d'ariane">
            <Link to="/" className="hover:text-[#FF4D00]">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/artisan" className="hover:text-[#FF4D00]">Artisans</Link>
            <span className="mx-2">/</span>
            <Link to={`/artisan/${m.slug}`} className="hover:text-[#FF4D00]">{m.label}</Link>
            <span className="mx-2">/</span>
            <span className="text-[#FF4D00]">{cityLabel}</span>
          </nav>
          <div className="inline-flex items-center gap-3">
            <div className="w-14 h-14 border-2 border-white bg-[#FF4D00] flex items-center justify-center">
              <Icon className="w-7 h-7 text-white" strokeWidth={2.5} />
            </div>
            <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// {m.label} · {cityLabel}</div>
          </div>
          <h1 className="display text-4xl lg:text-6xl tracking-tighter mt-5 max-w-4xl leading-[0.95]">
            {m.label} à <span className="text-[#FF4D00]">{cityLabel}</span>
          </h1>
          <p className="mt-6 text-zinc-300 text-base lg:text-lg max-w-3xl">
            {m.intro} À {cityLabel}, les {m.plural.toLowerCase()} Batisur sont SIRET vérifiés et interviennent en {cityLabel} et alentours.
          </p>
          {isProspected && (
            <div className="mt-6 inline-flex items-center gap-2 border-2 border-[#FF4D00] bg-[#FF4D00]/10 px-4 py-2">
              <Clock className="w-4 h-4 text-[#FF4D00]" />
              <span className="text-sm">Ville en cours de recrutement d'artisans. Publiez votre demande, nous cherchons pour vous !</span>
            </div>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/register?role=demandeur&metier=${m.slug}&ville=${citySlug}`}>
              <button className="btn-batisur flex items-center gap-2" data-testid={`metier-city-cta-${m.slug}-${citySlug}`}>
                Publier ma demande à {cityLabel} <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to={`/artisans/ville/${citySlug}`}>
              <button className="btn-anthracite border-2 border-white/40">
                Voir tous les artisans de {cityLabel}
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* QUICK MISSION FORM — high converting inline signup + mission */}
      <section className="max-w-4xl mx-auto px-6 py-16 border-b-2 border-[#0A0A0B]">
        <QuickMissionForm
          metierLabel={m.label}
          metierSlug={m.slug}
          cityLabel={cityLabel}
          citySlug={citySlug}
          context={`metier:${m.slug}:${citySlug}`}
        />
      </section>

      {/* PRIX à cette ville */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-b-2 border-[#0A0A0B]">
        <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Prix {cityLabel} 2026</div>
        <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 max-w-3xl leading-[0.95]">
          Prix d'un {m.label.toLowerCase()} à <span className="text-[#FF4D00]">{cityLabel}</span>
        </h2>
        {mult !== 1.0 && (
          <p className="mt-3 text-sm text-zinc-600">
            {mult > 1.0 ? (
              <>Les tarifs à {cityLabel} sont en moyenne <strong className="text-[#0A0A0B]">+{Math.round((mult - 1) * 100)}%</strong> par rapport à la moyenne France (coût de la vie et de la main d'œuvre).</>
            ) : (
              <>Les tarifs à {cityLabel} sont en moyenne <strong className="text-[#0A0A0B]">−{Math.round((1 - mult) * 100)}%</strong> par rapport à la moyenne France.</>
            )}
          </p>
        )}
        <div className="mt-10 grid md:grid-cols-3 gap-5" data-testid={`metier-city-pricing-${m.slug}-${citySlug}`}>
          <div className="border-2 border-[#0A0A0B] bg-white p-6 shadow-[3px_3px_0_#0A0A0B]">
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">// Tarif horaire</div>
            <div className="display text-3xl mt-2">{lowH}€ - {highH}€ <span className="text-base font-normal">/h</span></div>
            <p className="text-sm text-zinc-600 mt-2">Tarif horaire moyen HT à {cityLabel}, hors déplacement.</p>
          </div>
          <div className="border-2 border-[#0A0A0B] bg-white p-6 shadow-[3px_3px_0_#FF4D00]">
            <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Intervention</div>
            <div className="display text-3xl mt-2">{lowI}€ - {highI}€</div>
            <p className="text-sm text-zinc-600 mt-2">Intervention ponctuelle TTC à {cityLabel}.</p>
          </div>
          <div className="border-2 border-[#0A0A0B] bg-[#0A0A0B] text-white p-6">
            <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// {m.exampleProject.name}</div>
            <div className="display text-3xl mt-2 text-white">{lowP.toLocaleString('fr-FR')}€ - {highP.toLocaleString('fr-FR')}€</div>
            <p className="text-sm text-zinc-400 mt-2">Chantier complet TTC à {cityLabel}.</p>
          </div>
        </div>
        <p className="text-xs text-zinc-500 mt-6">
          * Prix indicatifs {cityLabel} 2026. Publiez votre demande gratuitement pour obtenir des devis fermes comparables de {m.plural.toLowerCase()} SIRET vérifiés à {cityLabel}.
        </p>
      </section>

      {/* SERVICES LOCAUX */}
      <section className="bg-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// {m.label} {cityLabel} · prestations</div>
          <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 max-w-3xl leading-[0.95]">
            Un {m.label.toLowerCase()} à {cityLabel} pour tous vos travaux
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {m.services.map((s, i) => (
              <div key={i} className="border-l-4 border-[#FF4D00] pl-5 py-1">
                <h3 className="display text-lg leading-tight">{s} à {cityLabel}</h3>
                <p className="text-xs text-zinc-500 mt-1">{m.plural} Batisur à {cityLabel}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 PROTECTIONS LOCALES */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-b-2 border-[#0A0A0B]">
        <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// 3 protections à {cityLabel}</div>
        <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 max-w-3xl leading-[0.95]">
          Payez votre {m.label.toLowerCase()} à {cityLabel} <span className="text-[#FF4D00]">sans risque.</span>
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {[
            { i: Lock, t: "Séquestre Stripe", d: `Vos fonds pour le ${m.label.toLowerCase()} à ${cityLabel} bloqués chez Stripe, libérés seulement après validation.` },
            { i: Camera, t: "Preuves photos", d: `Chaque étape à ${cityLabel} prouvée par photo GPS + timestamp SHA-256 opposable.` },
            { i: BadgeCheck, t: "SIRET vérifié Sirene", d: `${m.plural} de ${cityLabel} contrôlés en temps réel via l'API Sirene/INSEE.` },
          ].map(({ i: PI, t, d }, idx) => (
            <div key={idx} className="border-2 border-[#0A0A0B] bg-white p-6 shadow-[3px_3px_0_#0A0A0B]">
              <div className="w-12 h-12 border-2 border-[#0A0A0B] bg-[#FF4D00] flex items-center justify-center mb-4">
                <PI className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <div className="display text-lg leading-tight">{t}</div>
              <p className="text-sm text-zinc-700 mt-2">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ LOCAL */}
      <section className="bg-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// FAQ {cityLabel}</div>
          <h2 className="display text-3xl mt-3">{m.faqTitle}</h2>
          <p className="text-base text-zinc-700 mt-4 leading-relaxed">{m.faqBody}</p>
          <div className="mt-8 border-l-4 border-[#FF4D00] pl-5 py-2 bg-zinc-50">
            <p className="text-sm text-zinc-700">
              À <strong>{cityLabel}</strong>, publiez votre demande gratuitement en 5 minutes et recevez des devis fermes comparables de {m.plural.toLowerCase()} SIRET vérifiés dans votre secteur.
            </p>
          </div>
        </div>
      </section>

      {/* PROSPECT NOTIFY (only for prospected cities) */}
      {isProspected && (
        <section className="bg-[#F7F7F9] border-b-2 border-[#0A0A0B]">
          <div className="max-w-4xl mx-auto px-6 py-14">
            <CityProspectForm
              citySlug={citySlug}
              cityLabel={cityLabel}
              context="metier"
              metierSlug={m.slug}
            />
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="bg-[#FF4D00] text-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-5xl mx-auto px-6 py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-white/90 font-semibold">// {cityLabel} · Prêt ?</div>
            <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-2 leading-tight">
              Trouvez votre {m.label.toLowerCase()} à {cityLabel} en 5 minutes
            </h2>
          </div>
          <Link to={`/register?role=demandeur&metier=${m.slug}&ville=${citySlug}`}>
            <button className="btn-anthracite shadow-[4px_4px_0_#0A0A0B] flex items-center gap-2">
              Publier ma demande <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
